import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import RemoveMarkdown from "remove-markdown";
import { QuestionType } from "@reside/forms/dist/templateTypes";
import { getFieldName } from "@reside/forms/dist/templateUtils";
import { STATES } from "../../form/states-field";
import { ValidationBlockBoundary } from "../validation-block-boundary";
import { BlockField, useBlockComponentContext } from "../";
import { getChoices, getCustomOptionLabel, getCustomOptionLabelAsElement, } from "./helper";
// Form Fields
import { CheckboxGroupField } from "../../form/checkbox-group-field";
import { ComputationField } from "../../form/computation-field";
import { CurrencyField } from "../../form/currency-field";
import { DateFieldInput } from "../../form/date-field-input";
import { FileField } from "../../form/file-field";
import { RadioGroupField } from "../../form/radio-group-field";
import { SelectField } from "../../form/select-field";
import { SignatureField } from "../../form/signature-field";
import { SnapshotControlField } from "../../form/snapshot-control-field";
import { SSNField } from "../../form/ssn-field";
import { TextAreaField } from "../../form/text-area-field";
import { TextField } from "../../form/text-field";
import { getFormFieldComponent } from "../../form";
const defaultFormComponents = {
    CheckboxGroupField,
    ComputationField,
    CurrencyField,
    DateFieldInput,
    FileField,
    RadioGroupField,
    SelectField,
    SignatureField,
    SnapshotControlField,
    SSNField,
    TextAreaField,
    TextField,
};
export const TIME_ZONES = [
    {
        label: "New York (GMT-4)",
        value: "America/New_York",
    },
    {
        label: "Chicago (GMT-5)",
        value: "America/Chicago",
    },
    {
        label: "Denver (GMT-6)",
        value: "America/Denver",
    },
    {
        label: "Phoenix (GMT-7)",
        value: "America/Phoenix",
    },
    {
        label: "Los Angeles (GMT-7)",
        value: "America/Los_Angeles",
    },
    {
        label: "Anchorage (GMT-8)",
        value: "America/Anchorage",
    },
    {
        label: "Honolulu (GMT-10)",
        value: "Pacific/Honolulu",
    },
];
export const REPLACE_ADDRESS_STREET = "Address (234 Main st.)";
export const REPLACE_ADDRESS_NUMBER = "Address line 2 (Apartment, suite, unit)";
export const GO_LIVE_DATE_PLACEHOLDER = "Go Live Date (MM/DD/YYYY) (UTC) (Optional)";
export const MIDDLE_INITIAL_LABEL = "Middle Initial";
export const AUDIT_LOG_TEXT_LABEL = "Audit Log History Record Description";
export const AUDIT_LOG_TEXT_PLACEHOLDER = "Description in Audit Log history";
export const BlockFormControl = ({ reference, numberOfBlocks, format, keyboardType, signDateRef, state, queryVariables, onCatch, id, range, ...props }) => {
    const { CheckboxGroupField, ComputationField, CurrencyField, DateFieldInput, FileField, RadioGroupField, SelectField, SignatureField, SnapshotControlField, SSNField, TextAreaField, TextField, Translated, translate, Markdown, interpolate, } = { ...props, ...useBlockComponentContext() };
    const formik = useFormikContext();
    const fieldName = getFieldName(reference);
    const placeholder = useMemo(() => {
        if (typeof reference.id === "string" && reference.id.includes("auditLabel")) {
            return AUDIT_LOG_TEXT_PLACEHOLDER;
        }
        switch (reference.id) {
            case "facilityGoLiveDate":
                return GO_LIVE_DATE_PLACEHOLDER;
            case "facilityAddressStreet":
                return REPLACE_ADDRESS_STREET;
            case "facilityAddressNumber":
                return REPLACE_ADDRESS_NUMBER;
            case "resident_middleInitial":
                return MIDDLE_INITIAL_LABEL;
            default:
                return RemoveMarkdown(translate(reference.translationKey));
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reference.translationKey]);
    const markdownRenderer = useCallback(({ translation }) => (_jsx(Markdown, { values: { query: queryVariables, values: formik.values }, source: translation })), [formik.values, queryVariables]);
    const label = useMemo(() => {
        if (typeof reference.id === "string" && reference.id.includes("auditLabel")) {
            return AUDIT_LOG_TEXT_LABEL;
        }
        switch (reference.id) {
            case "facilityGoLiveDate":
                return GO_LIVE_DATE_PLACEHOLDER;
            case "facilityAddressStreet":
                return REPLACE_ADDRESS_STREET;
            case "facilityAddressNumber":
                return REPLACE_ADDRESS_NUMBER;
            case "resident_middleInitial":
                return MIDDLE_INITIAL_LABEL;
            default:
                return (_jsx(Translated, { translationKey: reference.translationKey, render: markdownRenderer }));
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reference.translationKey]);
    const fieldProps = {
        id,
        label,
        placeholder,
        disabled: state.disabled,
        clearable: state.clearable,
        name: fieldName,
        range,
    };
    const textFormatter = interpolate({
        query: queryVariables,
        values: formik.values,
    });
    const renderChildrenBlocks = useCallback(({ children }) => (_jsx(_Fragment, { children: children.map(block => (_jsx(BlockField, { block: block, queryVariables: queryVariables, onCatch: onCatch }, block.id))) })), [queryVariables]);
    const translatedChoices = useMemo(() => getChoices(reference, queryVariables, translationKey => (_jsx(Translated, { translationKey: translationKey, render: markdownRenderer }))), [markdownRenderer, queryVariables, reference]);
    const FormField = getFormFieldComponent(fieldName);
    const render = () => {
        switch (reference.type) {
            case QuestionType.TEXT: {
                if (format === "ssn") {
                    return _jsx(SSNField, { ...fieldProps });
                }
                else if (reference.computation) {
                    return (_createElement(ComputationField, { ...fieldProps, disabled: true, formatter: textFormatter, computation: reference.computation, key: `${reference.id}` }));
                }
                return (_jsx(TextField, { ...fieldProps, format: format, type: keyboardType, numberOfBlocks: numberOfBlocks }));
            }
            case QuestionType.TEXTAREA:
                return _jsx(TextAreaField, { ...fieldProps });
            case QuestionType.CURRENCY: {
                if (reference.computation) {
                    return (_jsx(ComputationField, { ...fieldProps, disabled: true, formatter: textFormatter, computation: reference.computation, currency: reference.currency || "USD" }));
                }
                return _jsx(CurrencyField, { ...fieldProps, currency: reference.currency });
            }
            case QuestionType.CHECKBOX: {
                /**
                 * Formik form.values are not in sync with template
                 * template utils computes state for blocks with delay
                 * Fields with option children can not use FastField optimizations
                 */
                const FormField = getFormFieldComponent(fieldName, translatedChoices);
                return reference.query ? (_jsx(CheckboxGroupField, { ...fieldProps, getOptionLabel: option => getCustomOptionLabelAsElement(reference, option, translate), getOptionValue: option => option.id, getOptionKey: (option, name) => `${name}.${option.id}`, isChecked: (option, fieldValue) => fieldValue.includes(option.id), options: translatedChoices, FormField: FormField, OptionChildren: renderChildrenBlocks })) : (_jsx(CheckboxGroupField, { ...fieldProps, options: translatedChoices, FormField: FormField, OptionChildren: renderChildrenBlocks }));
            }
            case QuestionType.DATE:
                return _jsx(DateFieldInput, { ...fieldProps });
            case QuestionType.RADIO: {
                /**
                 * Formik form.values are not in sync with template
                 * template utils computes state for blocks with delay
                 * Fields with option children can not use FastField optimizations
                 */
                const FormField = getFormFieldComponent(fieldName, translatedChoices);
                return reference.query ? (_jsx(RadioGroupField, { ...fieldProps, getOptionLabel: option => getCustomOptionLabelAsElement(reference, option, translate), getOptionValue: option => option.id, getRawOptionValue: option => option.id, getOptionKey: (option, name) => `${name}.${option.id}`, isChecked: (option, fieldValue) => fieldValue === option.id, options: translatedChoices, OptionChildren: renderChildrenBlocks, FormField: FormField })) : (_jsx(RadioGroupField, { ...fieldProps, options: translatedChoices, OptionChildren: renderChildrenBlocks, FormField: FormField }));
            }
            case QuestionType.SELECT:
                return (_jsx(SelectField, { ...fieldProps, FormField: FormField, getOptionLabel: option => {
                        return reference.query
                            ? getCustomOptionLabel(reference, option, translate)
                            : option.label;
                    }, getOptionValue: option => reference.query ? option.id : option.value, options: getChoices(reference, queryVariables, translate) }));
            case QuestionType.STATE_SELECT:
                return (_jsx(SelectField, { ...fieldProps, getOptionValue: option => option.id, options: STATES }));
            case QuestionType.TIMEZONE_SELECT:
                return _jsx(SelectField, { ...fieldProps, options: TIME_ZONES });
            case QuestionType.SIGNATURE:
                return _jsx(SignatureField, { ...fieldProps, signDateRef: signDateRef });
            case QuestionType.IMAGE:
                return _jsx(SnapshotControlField, { ...fieldProps });
            case QuestionType.FILE:
                return (_jsx(FileField, { ...fieldProps, acceptedMediaTypes: reference.acceptedMediaTypes, FormField: FormField, queryVariables: queryVariables }));
            default: {
                throw new Error(`Block.reference.type="${JSON.stringify(reference)}" does not have a renderer`);
            }
        }
    };
    return (_jsx(ValidationBlockBoundary, { name: fieldName, children: render() }));
};
BlockFormControl.defaultProps = {
    keyboardType: "text",
    ...defaultFormComponents,
};
